import React, { useCallback } from 'react';
import { View } from 'react-native';
import { IconEmail, IconHome, IconPhone, IconShieldDone, } from 'design-system/icons';
import { SettingsItemTitle, SettingsItemContent, SettingsItemNavigate, SettingsItemDescription, } from 'design-system/SettingsItem';
import { Spacer } from 'design-system';
import { SettingsSection } from 'design-system/SettingsSection';
import EmailVerificationStatusView from 'design-system/EmailVerificationStatusView';
import VerificationStatusView from 'design-system/VerificationStatusView';
import { formatAddress } from 'utils';
import { useUserAddress } from 'hooks';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import { useReSelector } from 'utils/hooksApi';
import useTrustedContacts, { getDisplayName, } from 'features/invest/hooks/useTrustedContacts';
import { selectIsGBUser } from 'reducers/selectors';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import AddressVerificationStatusView from 'design-system/AddressVerificationStatusView';
const strings = {
    contactDetails: 'Contact details',
    email: 'Email',
    addressVerified: 'Address verified',
    verifyYourAddress: 'Verify your address',
    enterEmail: 'Add...',
    phoneNumber: 'Phone number',
    verified: 'Verified',
    addPhoneNumber: 'Add...',
    addAddress: 'Choose...',
    address: 'Address',
    trustedContact: 'Trusted contact',
    add: 'Add...',
};
export const ContactDetailsSection = () => {
    const colors = useColors();
    const { navigateDeprecated } = useMainStackNavigation();
    const userCountry = useAppSelector((state) => state.user.user.guessedHomeCountry);
    const { primaryAddress } = useUserAddress();
    const isGBUser = useAppSelector(selectIsGBUser);
    const [trustedContacts] = useTrustedContacts();
    const { email, phoneNumber, isEmailVerified } = useReSelector((store) => ({
        email: store.user.user.email,
        phoneNumber: store.user.user.phoneNumber,
        isEmailVerified: store.user.user.isEmailVerified,
    }));
    const changeEmail = useCallback(() => {
        navigateDeprecated('ChangeDetail', { key: 'email' });
    }, []);
    const changePhoneNumber = useCallback(() => {
        navigateDeprecated('PhoneVerification', {});
    }, []);
    const changeAddress = useCallback(() => {
        if (isGBUser) {
            if (primaryAddress) {
                navigateDeprecated('AddressInfoWithStatusScreen');
            }
            else {
                navigateDeprecated('ChangeAddressProgressScreen', {
                    flow: 'ADD',
                });
            }
        }
        else {
            navigateDeprecated('EditAddress');
        }
    }, [isGBUser, primaryAddress]);
    const changeTrustedContact = useCallback(() => {
        navigateDeprecated('EditTrustedContact');
    }, []);
    const addressName = primaryAddress ? formatAddress(primaryAddress) : null;
    return (<SettingsSection name={strings.contactDetails}>
      <SettingsItemNavigate icon={<IconEmail />} onPress={changeEmail}>
        <SettingsItemContent>
          <SettingsItemDescription>{strings.email}</SettingsItemDescription>
          <SettingsItemTitle {...(!email && {
        color: colors.text.placeholder,
    })}>
            {email || strings.enterEmail}
          </SettingsItemTitle>
          {!!email && (<EmailVerificationStatusView verified={isEmailVerified}/>)}
        </SettingsItemContent>
      </SettingsItemNavigate>

      <SettingsItemNavigate icon={<IconPhone gradientKey="lime"/>} onPress={changePhoneNumber}>
        <SettingsItemContent>
          <SettingsItemDescription>
            {strings.phoneNumber}
          </SettingsItemDescription>
          <SettingsItemTitle {...(!phoneNumber && {
        color: colors.text.placeholder,
    })}>
            {phoneNumber || strings.addPhoneNumber}
          </SettingsItemTitle>
          {!!phoneNumber && (<VerificationStatusView state="verified" label={strings.verified}/>)}
        </SettingsItemContent>
      </SettingsItemNavigate>

      {(userCountry === 'GB' || userCountry === 'US') && (<View testID="AddressView">
          <SettingsItemNavigate icon={<IconHome gradientKey="ultimate"/>} onPress={changeAddress}>
            <SettingsItemContent>
              <SettingsItemDescription>
                {strings.address}
              </SettingsItemDescription>
              <SettingsItemTitle numberOfLines={1} {...(!addressName && {
            color: colors.text.placeholder,
        })}>
                {addressName || strings.addAddress}
              </SettingsItemTitle>
              {isGBUser && !!primaryAddress ? (<>
                  <Spacer h={4}/>
                  <AddressVerificationStatusView testID="AddressVerificationStatus" status={primaryAddress.kycStatus}/>
                </>) : null}
            </SettingsItemContent>
          </SettingsItemNavigate>
        </View>)}

      {trustedContacts !== undefined && (<SettingsItemNavigate icon={<IconShieldDone color={colors.gradients.skyBlue.colors[0]}/>} description={strings.trustedContact} onPress={changeTrustedContact} title={trustedContacts && trustedContacts[0]
                ? getDisplayName(trustedContacts[0])
                : strings.add}/>)}
    </SettingsSection>);
};
